import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  HomePage,
  TaskPage,
  SummaryPage,
  ListCasesPage,
  RecordingPage,
  AuthPage
} from './components';
import { getAuthToken } from './utils/auth';
import { useBrowserLocalRecordingRecoveryCleanup } from './hooks/useBrowserLocalRecordingRecoveryCleanup';
import { isInMaintenanceWindow } from './utils/maintenanceWindow';

// Redirect screen component with delayed support message
const RedirectScreen: React.FC = () => {
  const [showSupportMessage, setShowSupportMessage] = useState(false);
  const [userIP, setUserIP] = useState<string | null>(null);

  useEffect(() => {
    // Show support message after 5 seconds
    const timer = setTimeout(() => setShowSupportMessage(true), 5000);

    // Fetch user's IP address
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setUserIP(data.ip))
      .catch(() => setUserIP('onbekend'));

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center px-6">
        {/* Animated spinner */}
        <div className="mb-8">
          <svg className="w-16 h-16 mx-auto text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
        
        <h1 className="text-2xl font-semibold text-white mb-3">
          Even geduld...
        </h1>
        <p className="text-slate-400 text-lg">
          U wordt doorgestuurd naar de inlogpagina.
        </p>
        {showSupportMessage && (
          <p className="text-slate-400 text-lg mt-4">
            Als dit te lang duurt, neem dan contact op met de support van CompetenSYS. Uw IP adres ({userIP || 'laden...'}) moet misschien worden geverifieerd.
          </p>
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isMaintenanceWindow, setIsMaintenanceWindow] = useState(isInMaintenanceWindow());
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null);

  useBrowserLocalRecordingRecoveryCleanup();

  // Screen Wake Lock: Prevent screen from sleeping
  useEffect(() => {
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
          console.log('Wake Lock activated');
        } catch (err) {
          console.log('Wake Lock request failed:', err);
        }
      }
    };

    // Re-acquire wake lock when page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    // Request wake lock on mount
    requestWakeLock();

    // Listen for visibility changes to re-acquire lock
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Release wake lock on unmount
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Check maintenance window status periodically
  useEffect(() => {
    const checkMaintenanceWindow = () => {
      setIsMaintenanceWindow(isInMaintenanceWindow());
    };

    // Check immediately
    checkMaintenanceWindow();

    // Check every minute to catch transitions
    const interval = setInterval(checkMaintenanceWindow, 60000);

    return () => clearInterval(interval);
  }, []);

  if (!getAuthToken() && (window.location.pathname !== '/auth' && window.location.pathname !== '/auth/')) {
    //add a small delay to avoid race condition
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    const ssoUrl ='https://sys.competensys.nl/sso-login?client=conversation_compass&return_to=' + encodeURIComponent(returnTo);
    window.location.href = ssoUrl;
    return <RedirectScreen />
  }

  return (
    <>
      {/* Maintenance Window Warning Banner */}
      {isMaintenanceWindow && (
        <div className="bg-yellow-500 text-white px-4 py-3 shadow-lg z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm font-medium text-center">
              Het systeem is offline voor het verwerken van gesprekken tussen 17:00 en 09:00. U kunt een gesprek starten, maar deze worden pas later verwerkt.
            </p>
          </div>
        </div>
      )}
      
      <Router>
        <Routes>
          <Route path="/" element={<ListCasesPage />} />
          <Route path="/record" element={<HomePage />} />
          <Route path="/:conversationId/record" element={<RecordingPage />} />
          <Route path="/case/:caseId" element={<TaskPage />} />
          <Route path="/case/:caseId/summary" element={<SummaryPage />} />
          <Route path="/auth" element={<AuthPage />} />
        </Routes>
      </Router>
      <Toaster 
        position="bottom-right" 
        expand={true}
        richColors={true}
        closeButton={true}
      />
    </>
  );
};

export default App;
